import React, { useState } from 'react';
import Icon from '../common/Icon';
import StatusBadge from '../common/StatusBadge';
import { MATCH_STATUS_LABELS, MATCH_STATUS_COLORS } from '../../utils/constants';
import { toJalali } from '../../utils/jalali';
import { getFileUrl } from '../../utils/media';
import MatchDetailsModal from '../common/MatchDetailsModal';

/**
 * ردیف‌های خلاصه‌ی آمار (گلزن/پاس‌گل/کارت) هر بازیکن را برای نمایش
 * زیر نتیجه‌ی مسابقه می‌سازد. فقط بازیکنانی که آماری ثبت شده نمایش داده می‌شوند.
 */
function PlayerStatsBadges({ playerStatistics }) {
  if (!playerStatistics || playerStatistics.length === 0) return null;

  return (
    <div className="mt-1.5 flex flex-wrap gap-1">
      {playerStatistics.map((stat) => {
        const name = `${stat.player?.firstName || ''} ${stat.player?.lastName || ''}`.trim();
        const parts = [];

        if (stat.goals > 0) parts.push(`⚽×${stat.goals}`);
        if (stat.assists > 0) parts.push(`🎯×${stat.assists}`);
        if (stat.yellowCards > 0) parts.push('🟨');
        if (stat.redCards > 0) parts.push('🟥');

        if (parts.length === 0) return null;

        return (
          <span
            key={stat.id}
            className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600"
            title={name}
          >
            {name} {parts.join(' ')}
          </span>
        );
      })}
    </div>
  );
}

export function TeamCell({ club }) {
  const logo = getFileUrl(club?.logo);

  return (
    <div className="flex items-center gap-2">
      <div className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-full bg-slate-100 ring-1 ring-slate-200">
        {logo ? (
          <img src={logo} alt="" className="h-full w-full object-contain p-0.5" />
        ) : (
          <span className="text-[11px] font-black text-slate-400">{(club?.name || '?').charAt(0)}</span>
        )}
      </div>
      <span className="font-bold text-slate-700">{club?.name}</span>
    </div>
  );
}

export function ScorePill({ match }) {
  if (!match.result) return <span className="text-slate-300">—</span>;

  const { homeScore, awayScore, winnerClubId } = match.result;

  return (
    <span
      dir="ltr"
      className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1 font-mono text-sm font-black text-white"
      title={winnerClubId ? undefined : 'مساوی'}
    >
      {homeScore}
      <span className="text-white/40">:</span>
      {awayScore}
    </span>
  );
}

export default function MatchesTable({ matches, canManage, onRecordResult, onCancel, isAdmin = false, isPublic = false }) {
  const [detailsMatch, setDetailsMatch] = useState(null);

  const getStatusBadge = (status) => {
    const map = {
      SCHEDULED: 'pending',
      PLAYED: 'active',
      CANCELED: 'suspended',
      POSTPONED: 'inactive',
    };
    return <StatusBadge status={map[status] || 'inactive'} label={MATCH_STATUS_LABELS[status]} />;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-right text-sm">
        <thead className="bg-gradient-to-r from-emerald-50 to-white border-b border-slate-200">
          <tr>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">عنوان</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">میزبان</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">مهمان</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">نتیجه</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">تاریخ</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">محل</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">وضعیت</th>
            <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500">عملیات</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {matches.map((m) => (
            <tr
              key={m.id}
              onClick={() => setDetailsMatch(m)}
              className="cursor-pointer transition-colors duration-200 hover:bg-emerald-50/50"
              title="برای مشاهده‌ی جزئیات کامل کلیک کنید"
            >
              <td className="px-4 py-3 font-medium">{m.title}</td>
              <td className="px-4 py-3"><TeamCell club={m.homeClub} /></td>
              <td className="px-4 py-3"><TeamCell club={m.awayClub} /></td>
              <td className="px-4 py-3">
                <ScorePill match={m} />
                <PlayerStatsBadges playerStatistics={m.playerStatistics} />
              </td>
              <td className="px-4 py-3">{toJalali(m.date)}</td>
              <td className="px-4 py-3">{m.location}</td>
              <td className="px-4 py-3">{getStatusBadge(m.status)}</td>
              <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => setDetailsMatch(m)}
                  className="rounded p-1 text-sky-500 transition-colors hover:bg-sky-50 hover:text-sky-700"
                  title="جزئیات کامل مسابقه"
                >
                  <Icon name="eye" className="h-4 w-4" />
                </button>
                {canManage && m.status !== 'CANCELED' && (
                  <button
                    onClick={() => onRecordResult(m)}
                    className="rounded p-1 text-emerald-500 transition-colors hover:bg-emerald-50 hover:text-emerald-700"
                    title={m.status === 'PLAYED' ? 'ویرایش نتیجه' : 'ثبت نتیجه'}
                  >
                    <Icon name="check" className="h-4 w-4" />
                  </button>
                )}
                {canManage && m.status !== 'CANCELED' && (
                  <button
                    onClick={() => onCancel(m.id)}
                    className="rounded p-1 text-red-500 transition-colors hover:bg-red-50 hover:text-red-700"
                    title="لغو"
                  >
                    <Icon name="trash" className="h-4 w-4" />
                  </button>
                )}
              </td>
            </tr>
          ))}
          {matches.length === 0 && (
            <tr>
              <td colSpan={8} className="py-8 text-center text-slate-400">
                مسابقه‌ای یافت نشد
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <MatchDetailsModal
        open={Boolean(detailsMatch)}
        onClose={() => setDetailsMatch(null)}
        matchId={detailsMatch?.id}
        initialMatch={detailsMatch}
        isPublic={isPublic}
        isAdmin={isAdmin || Boolean(canManage)}
      />
    </div>
  );
}